import React from 'react';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useTranslation } from 'react-i18next';
import { Assignment, Person } from '@mui/icons-material';
import { Logout } from '@cnx-saas/design-system';
import { useAuth } from '@cnx-saas/react-auth';
import { UserType } from '@cnx-saas/common';
import { BeforeInstallPromptEvent } from '../before-install-prompt-event';
import { SidebarTranslationKeys } from '../translation-keys';
import { useUserCentricsCMP } from '../hooks';
import { SidebarItem, SidebarItemIcon } from './navigation-section.style';
import {
  ProfileWrapper,
  UserCompanyWrapper,
  UserInformationWrapper,
  UserNameWrapper,
} from './profile-section.style';
import { OptionalTooltip } from './optional-tooltip';
import { PwaInstallButton } from './pwa-install-section/pwa-install-button';

function ProfileSection({
  isSidebarOpen,
  userName,
  userCompanyName,
  onLogoutClick,
  onUserProfileClick,
  onUserManagementClick,
  pwaInstallEvent,
}: {
  isSidebarOpen: boolean;
  userName: string;
  userCompanyName: string;

  onLogoutClick: () => void;
  onUserProfileClick: () => void;
  onUserManagementClick: () => void;
  pwaInstallEvent: BeforeInstallPromptEvent | null;
}): JSX.Element {
  const { t } = useTranslation();

  const { user } = useAuth(true);

  const { openConsentDialog } = useUserCentricsCMP();

  return (
    <ProfileWrapper
      isSidebarOpen={isSidebarOpen}
      hasInstallButton={Boolean(pwaInstallEvent)}
    >
      <OptionalTooltip
        title="User Profile"
        placement="right"
        isVisible={!isSidebarOpen}
      >
        <SidebarItem
          isActive={false}
          isSidebarOpen={isSidebarOpen}
          onClick={onUserProfileClick}
          dataTestId="UserProfile"
        >
          <SidebarItemIcon isSidebarOpen={isSidebarOpen}>
            <Person />
          </SidebarItemIcon>
          <UserInformationWrapper>
            <UserNameWrapper>{userName}</UserNameWrapper>
            <UserCompanyWrapper>{userCompanyName}</UserCompanyWrapper>
          </UserInformationWrapper>
        </SidebarItem>
      </OptionalTooltip>
      {user.userType === UserType.TEAM_ADMIN && (
        <OptionalTooltip
          title={t(SidebarTranslationKeys.USER_MANAGEMENT)}
          placement="right"
          isVisible={!isSidebarOpen}
        >
          <SidebarItem
            isActive={false}
            isSidebarOpen={isSidebarOpen}
            onClick={onUserManagementClick}
          >
            <SidebarItemIcon isSidebarOpen={isSidebarOpen}>
              <Assignment />
            </SidebarItemIcon>
            {t(SidebarTranslationKeys.USER_MANAGEMENT, {
              defaultValue: 'User Management',
            })}
          </SidebarItem>
        </OptionalTooltip>
      )}
      <OptionalTooltip
        title={
          t(SidebarTranslationKeys.PRIVACY_SETTINGS, 'Privacy Settings') ??
          'Privacy Settings'
        }
        placement="right"
        isVisible={!isSidebarOpen}
      >
        <SidebarItem
          isActive={false}
          isSidebarOpen={isSidebarOpen}
          onClick={openConsentDialog}
        >
          <SidebarItemIcon isSidebarOpen={isSidebarOpen}>
            <AdminPanelSettingsIcon />
          </SidebarItemIcon>
          {t(SidebarTranslationKeys.PRIVACY_SETTINGS, 'Privacy Settings') ??
            'Privacy Settings'}
        </SidebarItem>
      </OptionalTooltip>

      <OptionalTooltip
        title={t(SidebarTranslationKeys.LOGOUT, 'Logout') ?? 'Logout'}
        placement="right"
        isVisible={!isSidebarOpen}
      >
        <SidebarItem
          isActive={false}
          isSidebarOpen={isSidebarOpen}
          onClick={onLogoutClick}
        >
          <SidebarItemIcon isSidebarOpen={isSidebarOpen}>
            <Logout />
          </SidebarItemIcon>
          {t(SidebarTranslationKeys.LOGOUT, 'Logout')}
        </SidebarItem>
      </OptionalTooltip>

      <PwaInstallButton
        pwaInstallEvent={pwaInstallEvent}
        isSidebarOpen={isSidebarOpen}
      />
    </ProfileWrapper>
  );
}

export { ProfileSection };
